/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * Shared country-picker + phone-number field for every place a contact's
 * phone number is typed: the Profile emergency-contacts screen, the
 * onboarding circle-of-trust screen, and the Walk Me Home manual-entry
 * fallback.
 *
 * Why this exists: defaulting the country to the device locale (the first
 * phone-normalization fix) still has a silent failure mode — a US-locale
 * user typing a contact's Indian mobile ("7036062559") with no "+91" gets
 * it parsed as a *valid* US number and stored wrong forever, with no error
 * shown anywhere. An explicit country picker next to the input, plus a
 * visible "this is what we're about to save" preview, makes the country
 * a decision the user actually makes and can see, not a device-locale
 * guess they never notice.
 *
 * - Typing a leading "+<code>" always wins: the picker auto-switches to
 *   match (detectCountryFromInput), so an explicitly-international number
 *   is never second-guessed by whatever country happens to be selected.
 * - The preview line only appears once the current (country, number) pair
 *   normalizes to a real E.164 number, and shows its flag + international
 *   formatting — a wrong-country result is visible before saving, not
 *   discovered later when the contact doesn't get a message.
 *
 * Deliberately a controlled, presentational component: the caller owns
 * both pieces of state (country, nationalNumber) and any submit-time
 * validation error, so each screen's existing save/validate flow is
 * untouched — this only changes how the phone value is captured.
 */

import { useId } from "react";
import type { CountryCode } from "libphonenumber-js";
import { detectCountryFromInput, normalizePhone } from "@/lib/phone";
import {
  allPhoneCountries,
  describeE164,
  phoneCountryByRegion,
} from "@/lib/phoneCountries";

export interface PhoneCountryFieldClassNames {
  container?: string;
  label?: string;
  row?: string;
  dialBox?: string;
  select?: string;
  input?: string;
  error?: string;
  preview?: string;
}

export interface PhoneCountryFieldProps {
  country: CountryCode;
  nationalNumber: string;
  onCountryChange: (country: CountryCode) => void;
  onNationalNumberChange: (value: string) => void;
  label?: string;
  /** Accessible name for the number input when no visible `label` is
   *  rendered (e.g. a compact form where the label would be redundant). */
  ariaLabel?: string;
  placeholder?: string;
  /** Submit-time validation error, shown as-is below the field. */
  error?: string | null;
  autoFocus?: boolean;
  classNames?: PhoneCountryFieldClassNames;
}

const DEFAULT_CLASSNAMES: Required<PhoneCountryFieldClassNames> = {
  container: "",
  label: "text-[12px] font-medium text-muted-foreground mb-1 block",
  row: "flex items-stretch gap-2",
  dialBox:
    "relative flex items-center rounded-xl border border-border bg-background px-3",
  select: "absolute inset-0 cursor-pointer opacity-0",
  input:
    "min-w-0 flex-1 rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/50",
  error: "text-[11px] text-destructive mt-1",
  preview: "text-[11px] text-muted-foreground mt-1",
};

export default function PhoneCountryField({
  country,
  nationalNumber,
  onCountryChange,
  onNationalNumberChange,
  label,
  ariaLabel,
  placeholder,
  error,
  autoFocus,
  classNames,
}: PhoneCountryFieldProps) {
  const numberId = useId();
  const selectId = useId();
  const cls = { ...DEFAULT_CLASSNAMES, ...classNames };
  const countries = allPhoneCountries();
  const selected = phoneCountryByRegion(country) ?? countries[0];

  const handleNumberChange = (raw: string) => {
    onNationalNumberChange(raw);
    // An explicit "+<code>" always wins over whatever's currently picked —
    // never leave the picker showing a country the typed number disagrees
    // with.
    const detected = detectCountryFromInput(raw);
    if (detected && detected !== country) {
      onCountryChange(detected);
    }
  };

  const result = nationalNumber.trim()
    ? normalizePhone(nationalNumber, country)
    : null;
  const preview =
    result && "e164" in result ? describeE164(result.e164) : null;

  return (
    <div className={cls.container}>
      {label && (
        <label htmlFor={numberId} className={cls.label}>
          {label}
        </label>
      )}
      <div className={cls.row}>
        <div className={cls.dialBox}>
          <label htmlFor={selectId} className="sr-only">
            Country
          </label>
          <span className="mr-1.5 text-base" aria-hidden="true">
            {selected?.flag}
          </span>
          <span className="text-sm font-semibold">{selected?.dialCode}</span>
          <select
            id={selectId}
            value={country}
            onChange={(e) => onCountryChange(e.target.value as CountryCode)}
            className={cls.select}
          >
            {countries.map((c) => (
              <option key={c.region} value={c.region}>
                {c.flag} {c.name} ({c.dialCode})
              </option>
            ))}
          </select>
        </div>
        <input
          id={numberId}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          value={nationalNumber}
          onChange={(e) => handleNumberChange(e.target.value)}
          placeholder={placeholder}
          aria-label={!label ? ariaLabel : undefined}
          className={cls.input}
        />
      </div>
      {error ? (
        <p className={cls.error} role="alert">
          {error}
        </p>
      ) : (
        preview && (
          <p className={cls.preview}>
            {preview.flag} {preview.formatted}
          </p>
        )
      )}
    </div>
  );
}
